import React, { useState } from "react";
import { Alert, Button, Segmented, Typography, Upload, message } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { RcFile } from "antd/es/upload";
import { JsonEditor, UpdateFunction } from "json-edit-react";
import {
  AssetIoConfigOutput,
  SiteIoConfiguration,
} from "../../../../services/client";
import { SiteIOConfigurationSchema } from "../../../../services/client/schemas.gen";
import { useNotify } from "../../../util-components/Notifications/notifications";
import { SiteIoDraft } from "./utils/siteIoDiffUtils";
import { downloadJson } from "../../../../util/downloadJson";
import { formatAssetNames } from "../../../../util/phaseRotations";
import { PhaseRotationWarning } from "./PhaseRotationWarning";
import { DisableForViewer } from "../../../common/DisableForViewer";

const { Title, Text, Paragraph } = Typography;

const controllerModeValues =
  SiteIOConfigurationSchema.properties.controller_mode.enum;
const phasePermutationModeValues =
  SiteIOConfigurationSchema.properties.phase_permutation_mode.enum;

function sentenceCase(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function toModeOptions<const Value extends string>(
  values: readonly Value[],
): { label: string; value: Value }[] {
  return values.map((value) => ({ label: sentenceCase(value), value }));
}

const controllerModeOptions = toModeOptions(controllerModeValues);
const phasePermutationModeOptions = toModeOptions(phasePermutationModeValues);

type EditableSiteIo = {
  asset_io_config: AssetIoConfigOutput[];
  controller_mode?: SiteIoConfiguration["controller_mode"];
  phase_permutation_mode?: SiteIoConfiguration["phase_permutation_mode"];
};

type SiteIoConfigEditorProps = {
  initialData?: SiteIoConfiguration | null;
  onChange: (data: SiteIoDraft) => void;
  site_id: string;
  /** Assets in the site configuration without a declared phase rotation. */
  undeclaredRotations: string[];
};

function validate(data: unknown): string[] {
  const errors: string[] = [];
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return ["Configuration must be a JSON object"];
  }
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.asset_io_config)) {
    errors.push("`asset_io_config` must be an array");
    return errors;
  }
  const seen = new Set<string>();
  obj.asset_io_config.forEach((entry, idx) => {
    if (entry === null || typeof entry !== "object") {
      errors.push(`asset_io_config[${idx}] must be an object`);
      return;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.asset_id !== "string" || e.asset_id.length === 0) {
      errors.push(
        `asset_io_config[${idx}].asset_id must be a non-empty string`,
      );
    } else if (seen.has(e.asset_id)) {
      errors.push(`Duplicate asset_id: ${e.asset_id}`);
    } else {
      seen.add(e.asset_id);
    }
    if (e.config === null || typeof e.config !== "object") {
      errors.push(`asset_io_config[${idx}].config must be an object`);
    } else if (typeof (e.config as Record<string, unknown>).type !== "string") {
      errors.push(`asset_io_config[${idx}].config.type must be a string`);
    }
  });
  if (
    obj.controller_mode !== undefined &&
    !(controllerModeValues as readonly string[]).includes(
      obj.controller_mode as string,
    )
  ) {
    errors.push(
      `controller_mode must be one of: ${controllerModeValues.join(", ")}`,
    );
  }
  if (
    obj.phase_permutation_mode !== undefined &&
    !(phasePermutationModeValues as readonly string[]).includes(
      obj.phase_permutation_mode as string,
    )
  ) {
    errors.push(
      `phase_permutation_mode must be one of: ${phasePermutationModeValues.join(", ")}`,
    );
  }
  return errors;
}

function toEditable(
  source: SiteIoConfiguration | EditableSiteIo | null | undefined,
): EditableSiteIo {
  if (!source) {
    return {
      asset_io_config: [],
      controller_mode: "read_only",
      phase_permutation_mode: "worst_case",
    };
  }
  return {
    asset_io_config: source.asset_io_config ?? [],
    controller_mode: source.controller_mode,
    phase_permutation_mode: source.phase_permutation_mode,
  };
}

export const SiteIoConfigEditor: React.FC<SiteIoConfigEditorProps> = ({
  initialData,
  onChange,
  site_id,
  undeclaredRotations,
}) => {
  const notify = useNotify();
  const [data, setData] = useState<EditableSiteIo>(() =>
    toEditable(initialData),
  );
  const [errors, setErrors] = useState<string[]>([]);

  const useTopologyAllowed = undeclaredRotations.length === 0;

  const applyData = (next: EditableSiteIo) => {
    setData(next);
    const validationErrors = validate(next);
    setErrors(validationErrors);
    if (validationErrors.length === 0) {
      onChange(next);
    }
  };

  const handleFileUpload = (file: RcFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (!e.target?.result) {
          notify({ message: "An empty file was uploaded.", type: "error" });
          return;
        }
        const parsed = JSON.parse(e.target.result as string);
        applyData(toEditable(parsed));
        message.success("File uploaded and parsed successfully!");
      } catch (_error) {
        message.error(
          "Failed to parse JSON file. Please check the file format.",
        );
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleFileDownload = () => {
    downloadJson(
      `${process.env.NODE_ENV}-site_io_configuration-${site_id}.json`,
      data,
    );
  };

  const handleTreeChange: UpdateFunction = (props) => {
    applyData(toEditable(props.newData as EditableSiteIo));
    return true;
  };

  return (
    <div style={{ padding: "20px" }}>
      <Title level={4}>Upload Site IO Configuration (JSON)</Title>
      <Paragraph type="secondary" className="w-full">
        Upload a JSON file containing the site IO configuration, or edit the
        parsed configuration directly below.
      </Paragraph>
      <DisableForViewer>
        <Upload
          beforeUpload={handleFileUpload}
          accept=".json"
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Upload JSON</Button>
        </Upload>
      </DisableForViewer>
      <Button
        icon={<DownloadOutlined />}
        onClick={handleFileDownload}
        className="ml-2"
      >
        Download current JSON
      </Button>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Text strong>Controller mode</Text>
          <Text type="secondary" className="text-xs">
            Read-only observes only; active lets the controller steer the site.
          </Text>
          <Segmented<NonNullable<EditableSiteIo["controller_mode"]>>
            className="self-start mt-1"
            value={data.controller_mode ?? "read_only"}
            onChange={(controller_mode) =>
              applyData({ ...data, controller_mode })
            }
            options={controllerModeOptions}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Text strong>Phase permutation mode</Text>
          <Text type="secondary" className="text-xs">
            Worst case assumes the least favorable phase mapping; use topology
            relies on the configured phase layout.
          </Text>
          <Segmented<NonNullable<EditableSiteIo["phase_permutation_mode"]>>
            className="self-start mt-1"
            value={data.phase_permutation_mode ?? "worst_case"}
            onChange={(phase_permutation_mode) =>
              applyData({ ...data, phase_permutation_mode })
            }
            options={phasePermutationModeOptions.map((option) =>
              option.value === "use_topology" &&
              !useTopologyAllowed &&
              data.phase_permutation_mode !== "use_topology"
                ? { ...option, disabled: true }
                : option,
            )}
          />
          {!useTopologyAllowed &&
            (data.phase_permutation_mode === "use_topology" ? (
              <PhaseRotationWarning
                undeclaredRotations={undeclaredRotations}
                className="mt-2"
              />
            ) : (
              <Text type="secondary" className="text-xs mt-1">
                Use topology is unavailable:{" "}
                {formatAssetNames(undeclaredRotations)} in the site
                configuration declare
                {undeclaredRotations.length === 1 ? "s" : ""} no phase rotation.
                Declare a rotation (identity counts) on every collector and
                device first.
              </Text>
            ))}
        </div>
      </div>

      {errors.length > 0 && (
        <Alert
          message="Validation Errors"
          description={
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          className="mt-4"
        />
      )}

      <div style={{ marginTop: "20px" }}>
        <Title level={5}>Parsed Configuration</Title>
        <Text type="secondary">
          Use the editor below to modify the configuration in case of validation
          errors.
        </Text>

        <pre style={{ background: "#f5f5f5", padding: "10px" }}>
          <JsonEditor
            data={data as unknown as object}
            onUpdate={handleTreeChange}
            collapse={2}
            minWidth={500}
            setData={(updated) =>
              applyData(toEditable(updated as EditableSiteIo))
            }
          />
        </pre>
      </div>
    </div>
  );
};
